import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, Plus, Search, Calendar, Building2, Users, FileText,
  Edit, Eye, Download, Filter, CheckCircle, XCircle, Clock,
  AlertCircle, ArrowRight, Receipt, Printer, Mail
} from 'lucide-react';
import { ResPDFGenerator } from '../../utils/pdfGenerator';

const Invoices = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const statusOptions = ['all', 'paid', 'pending', 'overdue', 'cancelled'];

  // Mock invoices data
  const [invoicesList, setInvoicesList] = useState([
    {
      id: 'INV-2024-001',
      invoiceNumber: 'INV-2024-001',
      rentalId: 'RENT-001',
      propertyId: 'PROP-001',
      propertyName: 'Riyadh Main Store',
      tenantId: 'TEN-001',
      tenantName: 'ABC Retail Company',
      issueDate: '2024-01-15',
      dueDate: '2024-02-15',
      daysUntilDue: 2,
      amount: 15000,
      status: 'pending',
      paymentDate: null,
      paymentMethod: null,
      description: 'Monthly rent payment - January 2024',
      period: 'January 2024',
      lateFee: 0,
      totalAmount: 15000,
      notes: 'Payment due on 15th of each month'
    },
    {
      id: 'INV-2024-002',
      invoiceNumber: 'INV-2024-002',
      rentalId: 'RENT-002',
      propertyId: 'PROP-002',
      propertyName: 'Jeddah Warehouse Complex',
      propertyInternalId: 'LOC-JED-001',
      tenantId: 'TEN-002',
      tenantName: 'XYZ Logistics',
      issueDate: '2024-01-01',
      dueDate: '2024-02-01',
      daysUntilDue: -11,
      amount: 25000,
      status: 'overdue',
      paymentDate: null,
      paymentMethod: null,
      description: 'Monthly rent payment - January 2024',
      period: 'January 2024',
      lateFee: 500,
      totalAmount: 25500,
      notes: 'Payment overdue, late fee applied'
    },
    {
      id: 'INV-2024-003',
      invoiceNumber: 'INV-2024-003',
      rentalId: 'RENT-003',
      propertyId: 'PROP-003',
      propertyName: 'Dammam Office Building',
      propertyInternalId: 'LOC-DMM-001',
      tenantId: 'TEN-003',
      tenantName: 'Tech Solutions Inc.',
      issueDate: '2024-01-10',
      dueDate: '2024-02-10',
      daysUntilDue: -3,
      amount: 18000,
      status: 'overdue',
      paymentDate: null,
      paymentMethod: null,
      description: 'Monthly rent payment - January 2024',
      period: 'January 2024',
      lateFee: 300,
      totalAmount: 18300,
      notes: 'Payment overdue, send reminder'
    },
    {
      id: 'INV-2023-050',
      invoiceNumber: 'INV-2023-050',
      rentalId: 'RENT-001',
      propertyId: 'PROP-001',
      propertyName: 'Riyadh Main Store',
      propertyInternalId: 'LOC-RYD-001',
      tenantId: 'TEN-001',
      tenantName: 'ABC Retail Company',
      issueDate: '2023-12-15',
      dueDate: '2024-01-15',
      daysUntilDue: null,
      amount: 15000,
      status: 'paid',
      paymentDate: '2024-01-15',
      paymentMethod: 'Bank Transfer',
      description: 'Monthly rent payment - December 2023',
      period: 'December 2023',
      lateFee: 0,
      totalAmount: 15000,
      notes: 'Payment received on time'
    },
    {
      id: 'INV-2024-004',
      invoiceNumber: 'INV-2024-004',
      rentalId: 'RENT-004',
      propertyId: 'PROP-005',
      propertyName: 'Khobar Storage Facility',
      propertyInternalId: 'LOC-KBU-001',
      tenantId: 'TEN-004',
      tenantName: 'Storage Co.',
      issueDate: '2024-01-15',
      dueDate: '2024-02-15',
      daysUntilDue: 2,
      amount: 20000,
      status: 'pending',
      paymentDate: null,
      paymentMethod: null,
      description: 'Monthly rent payment - January 2024',
      period: 'January 2024',
      lateFee: 0,
      totalAmount: 20000,
      notes: 'Payment due on 15th'
    },
  ]);

  const handleMarkAsPaid = (invoiceId) => {
    setInvoicesList(prev => prev.map(inv => 
      inv.id === invoiceId 
        ? { ...inv, status: 'paid', paymentDate: new Date().toISOString().split('T')[0], paymentMethod: 'Bank Transfer' }
        : inv
    ));
    if (selectedInvoice?.id === invoiceId) {
      setSelectedInvoice(prev => ({ ...prev, status: 'paid', paymentDate: new Date().toISOString().split('T')[0] }));
    }
  };

  const handleDownloadPDF = (invoice) => {
    const pdf = ResPDFGenerator.generateInvoice(invoice);
    pdf.save(`invoice_${invoice.invoiceNumber}.pdf`);
  };

  const handlePrintInvoice = (invoice) => {
    const pdf = ResPDFGenerator.generateInvoice(invoice);
    pdf.autoPrint();
    pdf.output('dataurlnewwindow');
  };

  const handleSendEmail = (invoice) => {
    // In a real app, this would trigger an email service
    alert(`Email would be sent to ${invoice.tenantName} for invoice ${invoice.invoiceNumber}`);
  };

  const handleGenerateInvoice = () => {
    // In a real app, this would open a form to generate new invoice
    alert('Invoice generation form would open here');
  };

  const filteredInvoices = invoicesList.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.propertyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.tenantName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || invoice.status === selectedFilter;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDueDateColor = (daysUntilDue) => {
    if (daysUntilDue === null) return 'text-gray-500';
    if (daysUntilDue < 0) return 'text-red-600 font-semibold';
    if (daysUntilDue <= 7) return 'text-orange-600 font-semibold';
    return 'text-green-600';
  };

  const InvoiceCard = ({ invoice }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer"
        whileHover={{ scale: 1.02, y: -2 }}
        onClick={() => setSelectedInvoice(invoice)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{invoice.invoiceNumber}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                {invoice.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-1">{invoice.propertyName}</p>
            <p className="text-sm text-gray-600 mb-4">{invoice.tenantName}</p>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Amount</span>
                <span className="text-lg font-semibold text-gray-900">SAR {invoice.totalAmount.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Issue Date</span>
                <span className="text-sm text-gray-900">{invoice.issueDate}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Due Date</span>
                <span className={`text-sm font-medium ${getDueDateColor(invoice.daysUntilDue)}`}>
                  {invoice.dueDate}
                  {invoice.daysUntilDue !== null && (
                    <span className="ml-2">
                      ({invoice.daysUntilDue < 0 ? `${Math.abs(invoice.daysUntilDue)} days overdue` :
                        invoice.daysUntilDue === 0 ? 'Due today' :
                        `${invoice.daysUntilDue} days`})
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Period</span>
                <span className="text-sm text-gray-900">{invoice.period}</span>
              </div>
              {invoice.status === 'paid' && invoice.paymentDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Payment Date</span>
                  <span className="text-sm text-green-600">{invoice.paymentDate}</span>
                </div>
              )}
              {invoice.lateFee > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Late Fee</span>
                  <span className="text-sm text-red-600">SAR {invoice.lateFee.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Invoices</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage rent invoices and payment tracking
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGenerateInvoice}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Generate Invoice
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <motion.button
              key={status}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === status
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Total Invoices',
            value: invoicesList.length,
            icon: Receipt,
            color: 'text-amber-600',
            bgColor: 'bg-amber-50'
          },
          {
            label: 'Paid',
            value: invoicesList.filter(i => i.status === 'paid').length,
            icon: CheckCircle,
            color: 'text-green-600',
            bgColor: 'bg-green-50'
          },
          {
            label: 'Pending',
            value: invoicesList.filter(i => i.status === 'pending').length,
            icon: Clock,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
          },
          {
            label: 'Overdue',
            value: invoicesList.filter(i => i.status === 'overdue').length,
            icon: AlertCircle,
            color: 'text-red-600',
            bgColor: 'bg-red-50'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                SAR {invoicesList.reduce((sum, i) => sum + i.totalAmount, 0).toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-gray-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paid Amount</p>
              <p className="text-2xl font-bold text-green-600">
                SAR {invoicesList
                  .filter(i => i.status === 'paid')
                  .reduce((sum, i) => sum + i.totalAmount, 0)
                  .toLocaleString()}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-2xl font-bold text-red-600">
                SAR {invoicesList
                  .filter(i => i.status !== 'paid' && i.status !== 'cancelled')
                  .reduce((sum, i) => sum + i.totalAmount, 0)
                  .toLocaleString()}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Invoices Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredInvoices.map((invoice) => (
          <InvoiceCard key={invoice.id} invoice={invoice} />
        ))}
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Invoice Details</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Invoice Number</label>
                  <p className="text-lg text-gray-900">{selectedInvoice.invoiceNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Property</label>
                  <p className="text-lg text-gray-900">{selectedInvoice.propertyName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Tenant</label>
                  <p className="text-lg text-gray-900">{selectedInvoice.tenantName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Issue Date</label>
                  <p className="text-lg text-gray-900">{selectedInvoice.issueDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Due Date</label>
                  <p className={`text-lg font-semibold ${getDueDateColor(selectedInvoice.daysUntilDue)}`}>
                    {selectedInvoice.dueDate}
                    {selectedInvoice.daysUntilDue !== null && (
                      <span className="ml-2 text-sm">
                        ({selectedInvoice.daysUntilDue < 0 ? `${Math.abs(selectedInvoice.daysUntilDue)} days overdue` :
                          selectedInvoice.daysUntilDue === 0 ? 'Due today' :
                          `${selectedInvoice.daysUntilDue} days`})
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Period</label>
                  <p className="text-lg text-gray-900">{selectedInvoice.period}</p>
                </div>
                {selectedInvoice.status === 'paid' && selectedInvoice.paymentDate && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment Date</label>
                    <p className="text-lg text-green-600">{selectedInvoice.paymentDate}</p>
                  </div>
                )}
              </div>

              {/* Amount Details */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Amount Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Rent Amount</span>
                    <span className="text-lg text-gray-900">SAR {selectedInvoice.amount.toLocaleString()}</span>
                  </div>
                  {selectedInvoice.lateFee > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Late Fee</span>
                      <span className="text-lg text-red-600">SAR {selectedInvoice.lateFee.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                    <span className="text-xl font-bold text-gray-900">SAR {selectedInvoice.totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="border-t border-gray-200 pt-6">
                <label className="text-sm font-medium text-gray-700">Description</label>
                <p className="text-lg text-gray-900 mt-2">{selectedInvoice.description}</p>
              </div>

              {selectedInvoice.notes && (
                <div className="border-t border-gray-200 pt-6">
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-lg text-gray-900 mt-2">{selectedInvoice.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6 flex gap-3 flex-wrap">
                {selectedInvoice.status !== 'paid' && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      handleMarkAsPaid(selectedInvoice.id);
                      setSelectedInvoice(null);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark as Paid
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handlePrintInvoice(selectedInvoice)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print Invoice
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSendEmail(selectedInvoice)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Send Email
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDownloadPDF(selectedInvoice)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
